import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {taskSetColour} from './taskSetColours';

/**
 * A pill, not a button-looking control: it sits inside a dense table row under
 * the task name, and anything with a border or a hover lift would compete with
 * the row's own controls. 19px tall so the row grows by one text line, no more.
 */
const Pill = styled.button<{background: string; foreground: string}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	height: 19px;
	padding: 0 8px 0 6px;
	border: none;
	border-radius: 9.5px;
	background: ${({background}) => background};
	color: ${({foreground}) => foreground};
	cursor: pointer;
	font-size: 11px;
	font-weight: 600;
	white-space: nowrap;
	flex-shrink: 0;

	&:focus-visible {
		outline: 2px solid ${({foreground}) => foreground};
		outline-offset: 1px;
	}
`;

export interface TaskSetChipProps {
	taskSetId: string;
	/** 1-based position of this instance in its set. */
	sequence?: number | null;
	/** How many instances the set has now. */
	instanceCount?: number | null;
	/**
	 * How many the rule says it produces. Preferred for the "of N" half because
	 * deleting an instance does not change what the rule promised, so a position
	 * stays coherent; the live count is the fallback for a bound with no count.
	 */
	plannedCount?: number | null;
	onOpen: (taskSetId: string) => void;
}

/**
 * Marks a task as an instance of a set, and opens that set's config view.
 *
 * Deliberately minimal: an icon, and the position in the set. It carries no set
 * name and no edited state. It is on every instance row, so anything more turns
 * a dense table into noise -- and the name is one click away in the view it
 * opens, which is where there is room for it.
 *
 * Being the way in is its third job, not an afterthought: the config view has two
 * entry points and this is the one that works while the reader is looking at the
 * work rather than at a list of rules.
 */
export const TaskSetChip = ({taskSetId, sequence, instanceCount, plannedCount, onOpen}: TaskSetChipProps) => {
	const intl = useIntl();
	const colour = taskSetColour(taskSetId);

	// Both halves or neither. "3 of" reads as truncated, and a bare "3" says
	// nothing about how long the series runs.
	//
	// A sequence past the count is dropped rather than shown. Sequence is fixed at
	// creation and the count is live, so deleting instances leaves the third of an
	// original twenty-six reading "3 of 2". The icon alone still says the task came
	// from a set, which is the part that is still true.
	// Counted against what the rule promised, not against how many survive. Sequence
	// is fixed at creation, so measuring it against a live count made the fourth of
	// four read "4 of 3" the moment an earlier instance was deleted -- and dropping
	// that label left one unlabelled chip beside numbered siblings, which reads as a
	// rendering fault. The rule's count does not move when an instance goes.
	const total = plannedCount != null ? plannedCount : instanceCount;
	// Still guarded: a bound with no count falls back to the live one, which a
	// deletion can put behind the sequence, and no label beats a false one.
	const hasCoherentPosition = sequence != null && total != null && sequence >= 1 && sequence <= total;
	const label = hasCoherentPosition ? intl.formatMessage({id: 'task_set.instance_sequence'}, {sequence, total}) : null;

	return (
		<Pill
			type="button"
			background={colour.background}
			foreground={colour.foreground}
			onClick={event => {
				// The row opens the task modal on click. Without this, marking an
				// instance would also open the task behind the drawer.
				event.stopPropagation();
				onOpen(taskSetId);
			}}
			data-cy="task-set-chip"
			aria-label={intl.formatMessage({id: 'task_set.manage'})}
			title={intl.formatMessage({id: 'task_set.manage'})}
		>
			<svg
				width="11"
				height="11"
				viewBox="0 0 16 16"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
				aria-hidden="true"
			>
				<path d="M3 8a5 5 0 0 1 8.4-3.7M13 8a5 5 0 0 1-8.4 3.7" />
				<path d="M11.6 2.2v2.4H9.2M4.4 13.8v-2.4h2.4" />
			</svg>
			{label ? <span>{label}</span> : null}
		</Pill>
	);
};
